/** Mirror of `functions/shared/ai-accounts.ts` — same copy-not-share convention as
 *  `grant-view.ts`. The server module owns the validation; this one owns rendering.
 *
 *  The rule this module exists to enforce: AI accounts are NEVER merged into the
 *  tenant list. Wherever both appear (the toolbar filter, the resource picker),
 *  they render as their own group, first, rather than interleaved alphabetically.
 */
import {GrantKind, isImpersonationKind} from './grant-view';

export interface AiAccount {
    /** AWS account id — the form a grant's `resource` carries. */
    id: string;
    /** Display label, and the alias operators use. */
    name: string;
}

/** dev first, prod second. */
export const AI_ACCOUNTS: readonly AiAccount[] = [
    {id: '550428926429', name: 'ennabl-ai2-dev'},
    {id: '306656769105', name: 'ennabl-ai2-prod'},
];

export const AI_ACCOUNT_IDS: readonly string[] = AI_ACCOUNTS.map(a => a.id);

export const AI_ACCOUNT_GROUP_LABEL = 'Ennabl AI accounts';
export const TENANT_GROUP_LABEL = 'Tenant accounts';

/** Matches an account id or its alias. The wildcard is never an AI account. */
export function isAiAccount(resource: string): boolean {
    if (resource === '*') {
        return false;
    }
    const needle = resource.trim().toLowerCase();
    return AI_ACCOUNTS.some(a => a.id === needle || a.name.toLowerCase() === needle);
}

/** Display label for an AI account resource; falls back to the raw value. */
export function aiAccountLabel(resource: string): string {
    const needle = resource.trim().toLowerCase();
    return AI_ACCOUNTS.find(a => a.id === needle || a.name.toLowerCase() === needle)?.name ?? resource;
}

/**
 * Is this (kind, resource) pair grantable? Mirrors the server check so the form
 * can block before submitting; the server remains the boundary.
 */
export function isValidResourceForKind(kind: GrantKind, resource: string): boolean {
    return !(isImpersonationKind(kind) && isAiAccount(resource));
}

/**
 * Label for the resource axis, which swaps with the access type: Data Access
 * targets AWS accounts (tenants AND the AI accounts), impersonation targets
 * tenants only.
 */
export function resourceAxisLabel(kind: GrantKind | 'mixed'): 'Account' | 'Tenant' | 'Subject' {
    if (kind === 'imp-ai') {
        // Ennabl AI does not target a tenant at all — it targets WHO you may log
        // in as (an address, a domain, or everyone).
        return 'Subject';
    }
    return kind === 'data-access' || kind === 'data-access-temporary' || kind === 'mixed'
        ? 'Account'
        : 'Tenant';
}

/** Column heading for the resource axis, per access type. */
export function resourceColumnHeading(kind: GrantKind | 'mixed'): string {
    switch (resourceAxisLabel(kind)) {
        case 'Subject':
            return 'Impersonate as';
        case 'Account':
            return 'Access to AWS accounts';
        case 'Tenant':
            return 'Access to tenants';
    }
}
